import ChevronRightIcon from '@assets/icons/chevron-right.svg';
import i18n from '@/utils/i18n';
import React, { useCallback, useMemo, useState } from 'react';
import {
  ACTION_SHEET_MAX_HEIGHT,
  BORDER_RADIUS_MEDIUM,
  SCREEN_PADDING,
  SCREEN_PADDING_LARGE,
  SCREEN_PADDING_SMALL,
  TYPOGRAPHY,
} from '@/styles';
import { AD_VIEWABILITY_CONFIG, useAdsContext } from '@/contexts/ads-context';
import { AdCampaignEventType, PackUnit } from '@/enums';
import { AdCampaignModuleType } from '@/types';
import {
  AdImpressionSource,
  DisplayAdModule,
} from '@/contexts/ads-context-types';
import { AdvertisedListItem } from './advertised-list-item';
import { APP_CONTAINER_ROUTES } from '@/navigators/routes';
import { AppContainerStackParams } from '@/navigators/types';
import { BaseActionSheet } from '@/components/base-action-sheet';
import { CachedImage } from '@usepepper/ui/atoms/cached-image';
import { CircledIcon } from '@/components/circled-icon';
import { createStyles } from '@/contexts/theme-context';
import { DisplayAdBanner } from './display-ad-banner';
import { DisplayAdDetailComponent } from './display-ad-detail-component';
import {
  FlatList,
  StyleProp,
  Text,
  TouchableOpacity,
  View,
  ViewStyle,
  ViewToken,
} from 'react-native';
import { HorizontalListArrows } from '@/components/horizontal-list-arrows';
import {
  ItemDetailContextProvider,
  useItemDetailContext,
} from '@/contexts/item-detail-context';
import {
  ItemListOption,
  ItemListOptionMetadata,
} from '@/contexts/item-context-types';
import { StackNavigationProp } from '@react-navigation/stack';
import { trackAdAnalytics } from '@/utils/ads';
import { useAdaptAndGroupVariantPacks } from '@/hooks/use-adapt-and-group-item-list-options';
import { useAuthenticatedUserContext } from '@/contexts/user-context';
import {
  useLayoutContext,
  useSmallLayoutSizeValue,
} from '@/contexts/layout-context';
import { useNavigation } from '@react-navigation/native';
import { useNewOrderActionsContext } from '@/contexts/new-order-actions-context';

type Navigation = StackNavigationProp<
  AppContainerStackParams,
  APP_CONTAINER_ROUTES.DisplayAd
>;

const DISPLAY_AD_BANNER_MAX_WIDTH_LARGE = 1120;

const DISPLAY_AD_BANNER_VP_UUID = 'DisplayAdBanner';
const DISPLAY_AD_BANNER_LIST_ITEM_WIDTH = 1;
const DISPLAY_AD_ITEM_LIMIT = 10;

interface Props {
  displayAd: DisplayAdModule;
  visibleUUIDs?: Set<string>;
}

export const DisplayAd: React.FC<Props> = ({ displayAd, visibleUUIDs }) => {
  const { debouncedUpdateQuantity } = useNewOrderActionsContext();
  const { onViewableAdItemsChanged } = useAdsContext();
  const { isSmallLayout, isLargeLayout } = useLayoutContext();
  const { navigate } = useNavigation<Navigation>();
  const { selectItemListOption } = useItemDetailContext();
  const [styles] = useStyles();
  const adaptAndGroupVariantPacks = useAdaptAndGroupVariantPacks();
  const {
    selectedRestaurantRelationship: { supplierUUID },
    selectedRestaurantUUID: restaurantUUID,
    user,
  } = useAuthenticatedUserContext();

  const [showAdDetailsSheet, setShowAdDetailsSheet] = useState(false);
  const displayAdContent = displayAd.content;

  const displayAdBannerListItem = useMemo(() => {
    const bannerListItemMetadata: ItemListOptionMetadata = {
      availabilityStatus: null,
      availableQuantity: null,
      baseUnit: null,
      category: null,
      currencyCode: null,
      displayAd: {
        ad_campaign_id: displayAd.campaignUUID,
        ad_manager_id: displayAd.adManagerUUID,
        advertiser_id: displayAd.advertiserUUID,
        display_ad_content: displayAd.content,
        gtin: '',
        variant_pack_event_data: {
          add_to_cart_event_id: '',
          click_event_id: displayAdContent.event_data.click_event_id,
          impression_event_id: displayAdContent.event_data.impression_event_id,
          purchase_event_id: '',
        },
      },
      displayName: '',
      externalID: null,
      itemUUID: '',
      maximumQuantity: null,
      minimumQuantity: null,
      outOfStockUUIDs: new Set(),
      packDisplayString: '',
      packUnit: PackUnit.Each,
      photoURLs: [],
      promotion: null,
      promotionPricePerUnitInMicros: null,
      skus: [],
      unitCount: 0,
      upcList: null,
      variantPackMetadata: {},
      variantPackUUID: DISPLAY_AD_BANNER_VP_UUID,
    };

    return {
      metadata: {
        ...bannerListItemMetadata,
        skus: [bannerListItemMetadata],
      },
      title: DISPLAY_AD_BANNER_VP_UUID,
      value: DISPLAY_AD_BANNER_VP_UUID,
    };
  }, [displayAd, displayAdContent]);

  const [detailsListData, carouselListData] = useMemo(() => {
    const adaptedItemListOption = adaptAndGroupVariantPacks(
      displayAd.variantPacks,
    );
    const withDisplayAds: ItemListOption[] = [];
    const withoutSampleRequests: ItemListOption[] = [];

    for (const item of adaptedItemListOption) {
      const { withDisplayAdSkus, withoutSampleRequestSkus } = (
        item.metadata?.skus || []
      ).reduce<{
        withDisplayAdSkus: ItemListOptionMetadata[];
        withoutSampleRequestSkus: ItemListOptionMetadata[];
      }>(
        (acc, sku) => {
          const displayAdEventURLData =
            displayAd.variantPackEventMap[sku.variantPackUUID];
          // Add displayAd metadata to fire display ad impressions
          const withDisplayAdSku = {
            ...sku,
            displayAd: displayAdEventURLData,
          };
          // Explicitly remove sampleRequest metadata to avoid firing
          // impressions when sample request button is not rendered
          const withoutSampleRequestSku = {
            ...withDisplayAdSku,
            sampleRequest: undefined,
          };

          acc.withDisplayAdSkus.push(withDisplayAdSku);
          acc.withoutSampleRequestSkus.push(withoutSampleRequestSku);
          return acc;
        },
        {
          withDisplayAdSkus: [],
          withoutSampleRequestSkus: [],
        },
      );

      withDisplayAds.push({
        ...item,
        metadata: {
          ...item.metadata,
          skus: withDisplayAdSkus,
        },
      } as ItemListOption);

      // Show items in the carousel if they're in the visibleUUIDs filter
      if (!visibleUUIDs || visibleUUIDs.has(String(item.value))) {
        withoutSampleRequests.push({
          ...item,
          metadata: {
            ...item.metadata,
            skus: withoutSampleRequestSkus,
          },
        } as ItemListOption);
      }
    }

    return [withDisplayAds, withoutSampleRequests];
  }, [adaptAndGroupVariantPacks, displayAd, visibleUUIDs]);

  const displayAdItemCarouselData = useMemo(() => {
    return [
      displayAdBannerListItem,
      ...carouselListData.slice(0, DISPLAY_AD_ITEM_LIMIT),
    ];
  }, [carouselListData, displayAdBannerListItem]);

  const showAdDetails = () => {
    trackAdAnalytics({
      adCampaignUUID: displayAd.campaignUUID,
      adManagerUUID: displayAd.adManagerUUID,
      adModule: AdCampaignModuleType.DisplayAd,
      advertiserUUID: displayAd.advertiserUUID,
      distributorUUID: supplierUUID,
      eventType: AdCampaignEventType.CLICK,
      eventUUID: displayAdContent.event_data.click_event_id,
      gtin: '',
      operatorUUID: restaurantUUID,
      source: 'display_ad_banner',
      userUUID: user.uuid,
      variantPackUUID: '',
    });

    if (isSmallLayout) {
      navigate(APP_CONTAINER_ROUTES.DisplayAd, {
        displayAd,
        listData: detailsListData,
      });
    } else {
      setShowAdDetailsSheet(true);
    }
  };

  // useCallback here since React Native seems to have issues with conflicting
  // `onViewableItemsChanged` callbacks for multiple lists that are mounted
  // https://github.com/facebook/react-native/issues/30171#issuecomment-1625551519
  const onViewableDisplayAdItemsChanged = useCallback(
    ({ viewableItems }: { viewableItems: ViewToken[] }) =>
      onViewableAdItemsChanged(viewableItems, AdImpressionSource.OrderGuide),
    [onViewableAdItemsChanged],
  );

  const containerStyle = useSmallLayoutSizeValue<StyleProp<ViewStyle>>(
    styles.container,
    styles.containerMedium,
  );

  const baseStyle = useSmallLayoutSizeValue<StyleProp<ViewStyle>>(
    styles.flex,
    styles.baseMedium,
  );

  const largeContainerStyle = isLargeLayout ? styles.containerLarge : null;
  const largeBaseStyle = isLargeLayout ? styles.baseLarge : null;

  return (
    <View style={[containerStyle, largeContainerStyle]}>
      <View style={[styles.base, baseStyle, largeBaseStyle]}>
        <TouchableOpacity focusable={false} onPress={showAdDetails}>
          <DisplayAdBanner displayAdContent={displayAdContent} />
        </TouchableOpacity>
        <View style={styles.carouselContainer}>
          <HorizontalListArrows<ItemListOption>
            arrowShape="round"
            displayAsStatic
            trackingKey="display_ad_carousel"
          >
            {(listProps) => (
              <FlatList<ItemListOption>
                {...listProps}
                alwaysBounceHorizontal={false}
                contentContainerStyle={styles.carouselContentContainer}
                data={displayAdItemCarouselData}
                decelerationRate="fast"
                horizontal
                ListFooterComponent={
                  // Render button if there are more items than the limit
                  carouselListData.length > DISPLAY_AD_ITEM_LIMIT ? (
                    <TouchableOpacity
                      onPress={showAdDetails}
                      style={styles.seeAllButton}
                    >
                      <CircledIcon
                        Icon={ChevronRightIcon}
                        iconHeight={18}
                        style={styles.seeAllIcon}
                      />
                      <Text style={TYPOGRAPHY.pSemiBold}>
                        {i18n.t('see_all_count', {
                          count: carouselListData.length,
                        })}
                      </Text>
                    </TouchableOpacity>
                  ) : null
                }
                onViewableItemsChanged={onViewableDisplayAdItemsChanged}
                renderItem={({ item }) => {
                  const displayAdVPData =
                    displayAd.variantPackEventMap[
                      item.metadata!.variantPackUUID
                    ]!;

                  return item.value === DISPLAY_AD_BANNER_VP_UUID ? (
                    // 1px list item to trigger banner impression
                    <View style={styles.bannerListItem} />
                  ) : (
                    <AdvertisedListItem
                      item={item}
                      onSelect={() => {
                        selectItemListOption(item, {
                          source: 'display_ad_banner',
                        });
                        trackAdAnalytics({
                          adCampaignUUID: displayAd.campaignUUID,
                          adManagerUUID: displayAd.adManagerUUID,
                          adModule: AdCampaignModuleType.DisplayAd,
                          advertiserUUID: displayAd.advertiserUUID,
                          distributorUUID: supplierUUID,
                          eventType: AdCampaignEventType.CLICK,
                          eventUUID:
                            displayAdVPData.variant_pack_event_data
                              .click_event_id,
                          gtin: displayAdVPData.gtin,
                          operatorUUID: restaurantUUID,
                          source: 'display_ad_banner',
                          userUUID: user.uuid,
                          variantPackUUID: displayAdVPData.variant_pack!.uuid, // Should always be initialized
                        });
                      }}
                      updateQuantity={(updateQuantityData) => {
                        debouncedUpdateQuantity(updateQuantityData, {
                          source: 'display_ad_banner',
                        });
                      }}
                    />
                  );
                }}
                showsHorizontalScrollIndicator={false}
                viewabilityConfig={AD_VIEWABILITY_CONFIG}
              />
            )}
          </HorizontalListArrows>
        </View>
      </View>
      {showAdDetailsSheet ? (
        <ItemDetailContextProvider>
          <BaseActionSheet
            contentStyle={styles.adDetailsSheetContent}
            isVisible
            modalStyle={styles.adDetailsSheetModal}
            onClose={() => setShowAdDetailsSheet(false)}
            title={
              displayAd.content.logo_image_url ? (
                <CachedImage
                  alt="" // TODO: make this configurable by advertiser, currently don't have meaningful default alt text
                  contentFit="contain"
                  source={{ uri: displayAd.content.logo_image_url }}
                  style={styles.logo}
                />
              ) : (
                (displayAd.content.title ?? '')
              )
            }
          >
            <DisplayAdDetailComponent
              displayAd={displayAd}
              listData={detailsListData}
            />
          </BaseActionSheet>
        </ItemDetailContextProvider>
      ) : null}
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  adDetailsSheetContent: {
    flex: 1,
  },
  adDetailsSheetModal: {
    flex: 1,
    maxHeight: ACTION_SHEET_MAX_HEIGHT,
  },
  bannerListItem: {
    width: DISPLAY_AD_BANNER_LIST_ITEM_WIDTH,
  },
  base: {
    backgroundColor: theme.colors.accentLight,
    overflow: 'hidden',
    paddingBottom: 8,
    width: '100%',
  },
  baseLarge: {
    borderRadius: BORDER_RADIUS_MEDIUM,
  },
  baseMedium: {
    maxWidth: '100%',
  },
  carouselContainer: {
    flexShrink: 1,
  },
  carouselContentContainer: {
    paddingLeft: SCREEN_PADDING_SMALL,
    paddingRight: 8,
    paddingVertical: 8,
  },
  container: {
    flexDirection: 'row',
    marginHorizontal: 0,
  },
  containerLarge: {
    marginHorizontal: SCREEN_PADDING,
    maxWidth: DISPLAY_AD_BANNER_MAX_WIDTH_LARGE,
  },
  containerMedium: {
    alignItems: 'flex-start',
    paddingBottom: 12,
    paddingTop: 4,
  },
  flex: {
    flex: 1,
  },
  logo: {
    aspectRatio: 1,
    height: 48,
  },
  seeAllButton: {
    alignItems: 'center',
    flex: 1,
    justifyContent: 'center',
    paddingHorizontal: SCREEN_PADDING_LARGE,
  },
  seeAllIcon: {
    backgroundColor: theme.colors.white,
    borderColor: theme.colors.white,
    marginBottom: 8,
  },
}));
